import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useLang } from "@/hooks/use-language";
import { VideoPlayer } from "@/components/VideoPlayer";
import { ProtectedContent } from "@/components/ProtectedContent";
import { QuizPlayer } from "@/components/QuizPlayer";
import { PrintableViewer } from "@/components/PrintableViewer";
import { useIsMobile } from "@/hooks/use-mobile";
import { getWatchItem } from "@/lib/content.functions";
import { ContentCharacterChips } from "@/components/CharacterAvatars";
import { AmbientControl } from "@/components/AmbientControl";
import { StoryNarrator } from "@/components/StoryNarrator";
import { useSmartBack } from "@/hooks/use-smart-back";
import { coverFor } from "@/assets/worlds";
import { ArrowLeft, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/watch/$id")({
  component: WatchPage,
  errorComponent: ({ error, reset }) => (
    <div className="grid min-h-screen place-items-center p-6 text-center">
      <div>
        <h2 className="text-lg font-semibold">{error.message}</h2>
        <button onClick={reset} className="mt-3 rounded-full bg-primary px-4 py-2 text-sm text-primary-foreground">
          Retry
        </button>
      </div>
    </div>
  ),
  notFoundComponent: () => (
    <div className="grid min-h-screen place-items-center">Content not found</div>
  ),
});

type Item = Awaited<ReturnType<typeof getWatchItem>>;

function WatchPage() {
  const { id } = Route.useParams();
  const { lang, dir } = useLang();
  const nav = useNavigate();
  const goBack = useSmartBack(() => nav({ to: "/app" }));
  const fetchItem = useServerFn(getWatchItem);
  const isMobile = useIsMobile();
  const [activeChildId, setActiveChildId] = useState<string | null>(null);
  const [watermarkText, setWatermarkText] = useState<string>("");

  const {
    data: item = null,
    isLoading: loading,
    error,
  } = useQuery<Item>({
    queryKey: ["watch", id],
    queryFn: () => fetchItem({ data: { contentId: id } }),
    // Per-item: long enough that re-opening feels instant, short enough that
    // signed URLs stay valid (media bucket TTL is typically ~10m).
    staleTime: 8 * 60_000,
  });

  useEffect(() => {
    if (error) toast.error(error instanceof Error ? error.message : "Failed to load");
  }, [error]);

  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (u.user) {
        // Watermark must never contain PII (email, name) — those show up in
        // screenshots and screen recordings of premium content. Use a short,
        // non-identifying account fingerprint that we can still reverse-map
        // internally from the audit log.
        setWatermarkText(`Jozori • ${u.user.id.slice(-6).toUpperCase()}`);
      }
    })();
    if (typeof window !== "undefined") {
      setActiveChildId(localStorage.getItem("jozori_active_child"));
    }
  }, []);

  const isAr = lang === "ar";

  if (loading)
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30" dir={dir}>
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-border/60 bg-background/80 px-4 py-3 backdrop-blur">
          <div className="h-10 w-10 rounded-full bg-muted animate-pulse" />
          <div className="h-4 w-1/3 rounded bg-muted animate-pulse" />
        </header>
        <main className="mx-auto max-w-4xl space-y-4 px-4 py-4">
          <div className="aspect-video w-full rounded-3xl bg-muted animate-pulse" />
          <div className="rounded-3xl border border-border bg-card p-5 space-y-3">
            <div className="h-5 w-2/3 rounded bg-muted animate-pulse" />
            <div className="h-3 w-full rounded bg-muted/70 animate-pulse" />
            <div className="h-3 w-5/6 rounded bg-muted/70 animate-pulse" />
          </div>
        </main>
      </div>
    );

  if (!item) {
    return (
      <div className="grid min-h-screen place-items-center p-6 text-center">
        <div>
          <h2 className="text-xl font-semibold">{isAr ? "غير متوفر" : "Not available"}</h2>
          <Link to="/app" className="mt-4 inline-block text-primary underline">
            {isAr ? "العودة" : "Back"}
          </Link>
        </div>
      </div>
    );
  }

  const locked = item.locked;
  const title = isAr ? item.title_ar : item.title_en;
  const desc = isAr ? item.description_ar : item.description_en;
  const body = isAr ? item.body_ar : item.body_en;
  const videoSrc = item.hls_url || item.media_url || "";
  const coverSrc = coverFor({
    cover_url: item.cover_url,
    world_id: item.world_id,
    age_group: item.age_group,
    slug: item.slug,
    character_code: item.character_code,
    character_avatar: item.character_avatar,
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 pb-12" dir={dir}>
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-border/60 bg-background/80 px-4 py-3 backdrop-blur">
        <button
          onClick={goBack}
          className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background"
          aria-label={isAr ? "رجوع" : "Back"}
        >
          <ArrowLeft className={`h-5 w-5 ${isAr ? "rotate-180" : ""}`} />
        </button>
        <h1 className="line-clamp-1 flex-1 text-base font-semibold">{title}</h1>
        <AmbientControl />
      </header>

      <main className="mx-auto max-w-4xl space-y-4 px-4 py-4">
        {locked ? (
          <div className="rounded-3xl border border-border bg-card p-8 text-center shadow-lg">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-primary/10 text-primary">
              <Lock className="h-7 w-7" />
            </div>
            <h2 className="mt-4 text-xl font-bold">
              {isAr ? "محتوى مميّز" : "Premium content"}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {isAr
                ? "افتح كل القصص والمحتوى بخطة العائلة."
                : "Unlock all stories and content with a family plan."}
            </p>
            <Link
              to="/app/pricing"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground"
            >
              <Sparkles className="h-4 w-4" />
              {isAr ? "اطلع على الخطط" : "See plans"}
            </Link>
          </div>
        ) : item.kind === "quiz" && item.quiz_data ? (
          <QuizPlayer
            contentId={item.id}
            childId={activeChildId}
            quiz={item.quiz_data as never}
            isAr={isAr}
            onComplete={(passed) =>
              toast.success(
                passed
                  ? isAr ? "أحسنت! 🎉" : "Great job! 🎉"
                  : isAr ? "حاول مجددًا!" : "Try again!",
              )
            }
          />
        ) : item.kind === "printable" && item.pdf_url ? (
          <PrintableViewer
            contentId={item.id}
            childId={activeChildId}
            pdfUrl={item.pdf_url}
            title={title ?? ""}
            isAr={isAr}
            isMobile={isMobile}
          />
        ) : (
          <ProtectedContent
            tier={item.tier as "free" | "family" | "premium"}
            watermarkText={watermarkText}
            disableVisibilityBlur={item.kind === "video"}
            className="overflow-hidden rounded-3xl"
          >
            {item.kind === "video" && videoSrc ? (
              <VideoPlayer
                contentId={item.id}
                childId={activeChildId}
                src={videoSrc}
                poster={coverSrc ?? undefined}
                subtitleEn={item.subtitle_en_url}
                subtitleAr={item.subtitle_ar_url}
                onComplete={() => toast.success(isAr ? "أحسنت! 🎉" : "Great job! 🎉")}
              />
            ) : item.embed_url ? (
              <div className="aspect-video w-full overflow-hidden rounded-3xl bg-black shadow-2xl">
                <iframe
                  src={item.embed_url}
                  title={title}
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  sandbox="allow-scripts allow-same-origin allow-presentation"
                />
              </div>
            ) : coverSrc ? (
              <div className="aspect-video w-full overflow-hidden rounded-3xl bg-muted">
                <img
                  src={coverSrc}
                  alt={title}
                  className="h-full w-full object-cover"
                  loading="eager"
                  decoding="async"
                  onContextMenu={(e) => e.preventDefault()}
                  draggable={false}
                />
              </div>
            ) : null}
          </ProtectedContent>
        )}

        <section className="rounded-3xl border border-border bg-card p-5 shadow-sm">
          <h2 className="text-lg font-bold">{title}</h2>
          {desc && <p className="mt-2 text-sm text-muted-foreground">{desc}</p>}
          {!locked && body && (
            <div className="mt-3">
              <StoryNarrator
                text={body}
                lang={isAr ? "ar" : "en"}
                label={isAr ? "استمع للقصة" : "Listen to story"}
              />
            </div>
          )}
          <div className="mt-3">
            <ContentCharacterChips contentId={item.id} isAr={isAr} />
          </div>
          {!locked && body && (
            <ProtectedContent
              tier={item.tier as "free" | "family" | "premium"}
              watermarkText={watermarkText}
              className="mt-4 rounded-2xl"
            >
              <article
                className={`mx-auto max-w-prose whitespace-pre-wrap text-foreground ${
                  isAr
                    ? "font-display-ar text-right text-[19px] leading-[2] tracking-normal"
                    : "text-[17px] leading-[1.75]"
                }`}
                dir={isAr ? "rtl" : "ltr"}
                onCopy={item.tier !== "free" ? (e) => e.preventDefault() : undefined}
              >
                {body}
              </article>
            </ProtectedContent>
          )}
          {/* Offline downloads are intentionally removed — paid content stays inside the app
              via the encrypted offline cache. See src/lib/offline.functions.ts. */}
        </section>
      </main>
    </div>
  );
}
